import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Check, CheckCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { MentionText } from '@/components/mentions/MentionText';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';
import { ContactList } from '@/components/messages/ContactList';
import { MessageReactions } from '@/components/messages/MessageReactions';

interface Conversation {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

export default function Messages() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [selectedUserProfile, setSelectedUserProfile] = useState<{
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const [messageReactions, setMessageReactions] = useState<Record<string, MessageReaction[]>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (user) {
      loadConversations();
      subscribeToMessages();
      subscribeToReactions();
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation);
      markMessagesAsRead(selectedConversation);
      loadSelectedUserProfile(selectedConversation);
      loadReactions();
    } else {
      setSelectedUserProfile(null);
      setMessageReactions({});
    }
  }, [selectedConversation, conversations]);

  const loadSelectedUserProfile = async (userId: string) => {
    // Check if profile exists in conversations first
    const existingProfile = conversations.find(c => c.id === userId);

    if (existingProfile) {
      setSelectedUserProfile({
        id: existingProfile.id,
        username: existingProfile.username,
        display_name: existingProfile.display_name,
        avatar_url: existingProfile.avatar_url
      });
    } else {
      // Fetch profile from database for new conversations
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .eq('id', userId)
        .single();

      if (!error && data) {
        setSelectedUserProfile(data);
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const subscribeToMessages = () => {
    const channel = supabase
      .channel(`direct-messages-${user?.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'direct_messages',
          filter: `receiver_id=eq.${user?.id}`,
        },
        () => {
          loadConversations();
          if (selectedConversation) {
            loadMessages(selectedConversation);
          }
        }
      )
      .on(
        'broadcast',
        { event: 'typing' },
        (payload) => {
          if (payload.payload.userId === selectedConversation && payload.payload.isTyping) {
            setIsOtherUserTyping(true);
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }
            typingTimeoutRef.current = setTimeout(() => {
              setIsOtherUserTyping(false);
            }, 3000);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const subscribeToReactions = () => {
    const channel = supabase
      .channel('message-reactions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reactions',
        },
        () => {
          loadReactions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const sendTypingIndicator = (isTyping: boolean) => {
    if (!user || !selectedConversation) return;
    
    supabase
      .channel(`direct-messages-${selectedConversation}`)
      .send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId: user.id, isTyping },
      });
  };

  const loadConversations = async () => {
    if (!user) return;

    const { data: sentMessages } = await supabase
      .from('direct_messages')
      .select('receiver_id, created_at, message')
      .eq('sender_id', user.id)
      .order('created_at', { ascending: false });

    const { data: receivedMessages } = await supabase
      .from('direct_messages')
      .select('sender_id, created_at, message, is_read')
      .eq('receiver_id', user.id)
      .order('created_at', { ascending: false });

    const userIds = new Set<string>();
    if (sentMessages) sentMessages.forEach(m => userIds.add(m.receiver_id));
    if (receivedMessages) receivedMessages.forEach(m => userIds.add(m.sender_id));

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url')
      .in('id', Array.from(userIds));

    const conversationsMap = new Map<string, Conversation>();

    profiles?.forEach(profile => {
      const userMessages = [
        ...(sentMessages?.filter(m => m.receiver_id === profile.id) || []),
        ...(receivedMessages?.filter(m => m.sender_id === profile.id) || []),
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      const lastMessage = userMessages[0];
      const unreadCount = receivedMessages?.filter(
        m => m.sender_id === profile.id && !m.is_read
      ).length || 0;

      conversationsMap.set(profile.id, {
        id: profile.id,
        username: profile.username,
        display_name: profile.display_name || profile.username,
        avatar_url: profile.avatar_url || '',
        last_message: lastMessage?.message || '',
        last_message_time: lastMessage?.created_at || '',
        unread_count: unreadCount,
      });
    });

    setConversations(Array.from(conversationsMap.values()));
  };

  const loadMessages = async (userId: string) => {
    if (!user) return;

    const { data } = await supabase
      .from('direct_messages')
      .select('*')
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${userId}),and(sender_id.eq.${userId},receiver_id.eq.${user.id})`)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data);
    }
  };

  const loadReactions = async () => {
    if (!selectedConversation || !user) return;

    const messageIds = messages.map(m => m.id);
    if (messageIds.length === 0) return;

    const { data } = await supabase
      .from('message_reactions')
      .select('*')
      .in('message_id', messageIds);

    if (data) {
      const reactionsMap: Record<string, MessageReaction[]> = {};
      data.forEach(reaction => {
        if (!reactionsMap[reaction.message_id]) {
          reactionsMap[reaction.message_id] = [];
        }
        reactionsMap[reaction.message_id].push(reaction);
      });
      setMessageReactions(reactionsMap);
    }
  };

  const getGroupedReactions = (messageId: string) => {
    const reactions = messageReactions[messageId] || [];
    const grouped: Record<string, { emoji: string; users: string[]; count: number }> = {};

    reactions.forEach(reaction => {
      if (!grouped[reaction.emoji]) {
        grouped[reaction.emoji] = {
          emoji: reaction.emoji,
          users: [],
          count: 0,
        };
      }
      grouped[reaction.emoji].users.push(reaction.user_id);
      grouped[reaction.emoji].count++;
    });

    return Object.values(grouped).map(g => ({
      id: g.emoji,
      emoji: g.emoji,
      user_id: user?.id || '',
      count: g.count,
      users: g.users,
    }));
  };

  const markMessagesAsRead = async (senderId: string) => {
    if (!user) return;

    await supabase
      .from('direct_messages')
      .update({ is_read: true })
      .eq('sender_id', senderId)
      .eq('receiver_id', user.id)
      .eq('is_read', false);
  };

  const sendMessage = async () => {
    if (!user || !selectedConversation || !newMessage.trim()) return;

    const { error } = await supabase.from('direct_messages').insert({
      sender_id: user.id,
      receiver_id: selectedConversation,
      message: newMessage.trim(),
    });

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message',
        variant: 'destructive',
      });
      return;
    }

    setNewMessage('');
    loadMessages(selectedConversation);
    loadConversations();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <BackButton />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-12rem)]">
          <Card className="md:col-span-1 flex flex-col overflow-hidden">
            <ContactList
              conversations={conversations}
              selectedConversation={selectedConversation}
              onSelectConversation={setSelectedConversation}
              onNewConversation={loadConversations}
            />
          </Card>

        <Card className="md:col-span-2 flex flex-col">
          {selectedUserProfile ? (
            <>
              <div className="p-4 border-b">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={selectedUserProfile.avatar_url || ''} />
                    <AvatarFallback>
                      {(selectedUserProfile.display_name || selectedUserProfile.username)[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{selectedUserProfile.display_name || selectedUserProfile.username}</p>
                    <p className="text-sm text-muted-foreground">
                      @{selectedUserProfile.username}
                    </p>
                  </div>
                </div>
              </div>

              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <>
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`mb-4 flex flex-col ${
                          message.sender_id === user?.id ? 'items-end' : 'items-start'
                        }`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            message.sender_id === user?.id
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <MentionText text={message.message} />
                          <div className="flex items-center gap-1 mt-1">
                            <p className="text-xs opacity-70">
                              {new Date(message.created_at).toLocaleTimeString()}
                            </p>
                            {message.sender_id === user?.id && (
                              <span className="text-xs opacity-70">
                                {message.is_read ? (
                                  <CheckCheck className="h-3 w-3 inline" />
                                ) : (
                                  <Check className="h-3 w-3 inline" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        {user && (
                          <MessageReactions
                            messageId={message.id}
                            currentUserId={user.id}
                            reactions={getGroupedReactions(message.id)}
                            onReactionUpdate={loadReactions}
                          />
                        )}
                      </div>
                    ))}
                    {isOtherUserTyping && (
                      <div className="flex justify-start mb-4">
                        <div className="bg-muted rounded-lg px-4 py-2">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 bg-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 bg-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div ref={messagesEndRef} />
              </ScrollArea>

              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      sendTypingIndicator(e.target.value.length > 0);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    onBlur={() => sendTypingIndicator(false)}
                    placeholder="Type a message..."
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} disabled={!newMessage.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a conversation to start messaging
            </div>
          )}
        </Card>
      </div>
      </div>
    </div>
  );
}
